/**
 *
 * Check incompatibility rules between hair and accessories.
 *
 * @author Matt Haynes <Matthew.haynes@four.agency>
 * @package Minifigs
 */

/*
* Rules:
If customer selects an item from ‘Over the neck accessories - front’ collection and an item from ‘Hair - front’ collection, show the warning.

If customer selects an item from ‘Over the neck accessories - back’ collection and an item from ‘Hair - back’, show the warning.

If customer selects TWO items from either of the over the neck collections, show the warning.

If customer selects an item from ‘Frames’ and an item from ‘Incompatible with Frames’, show the warning.

If a customer selects any other products from the 'Long Beards' collection AND they also select a hair from ‘Incompatible Hairs with Beards EXCEPT "LONG LEGO BEARD"‘, show warning message

If a customer selects a hair from `Long Hair` or `Mid Length Hair` collection and an accessory from either of the `Over the neck accessories` collections, show warning message

If a customer chooses a product in the ‘Tiaras and Veil COMPATIBLE with Hair’ AND they also choose a hair piece which is NOT in the collection ‘Hairs Compatible with Tiaras and Veils’, show warning.

If a customer selects a beard head from the `BEARD-HEADS` collection and a product from `Over the neck accessories - front`, show warning.
*
*/

const ACCESSORY_INCOMPATIBLE_TAGS = ['Incompatible with long hair', 'Incompatible with medium hair'];
const HAIR_WARNING_TAG = 'Show long hair warning';

const COLL_OVER_NECK_FRONT = 'Over the neck accessories - front';
const COLL_OVER_NECK_BACK = 'Over the neck accessories - back';
const COLL_HAIR_FRONT = 'Hair - front';
const COLL_HAIR_BACK = 'Hair - back';
const COLL_FRAMES = 'Frames';
const COLL_FRAMES_PERSONALISED = 'Frames: add personalised brick';
const COLL_INCOMPATIBLE_WITH_FRAMES = 'Incompatible with Frames';
const COLL_KEYCHAINS = 'Keychains';
const COLL_INCOMPATIBLE_WITH_KEYCHAINS = 'Incompatible with KEYCHAINS';
const COLL_LONG_BEARDS = 'Long Beards';
// Note: collection name includes quotes exactly as provided by Minifigs
const COLL_INCOMPATIBLE_BEARD_HAIRS = 'Incompatible Hairs with Beards EXCEPT "LONG LEGO BEARD"';
const COLL_TIARAS_VEIL_COMPATIBLE_WITH_HAIR = 'Tiaras and Veil COMPATIBLE with Hair';
const COLL_HAIRS_COMPATIBLE_WITH_TIARAS_VEILS = 'Hairs Compatible with Tiaras and Veils';
const COLL_LONG_HAIR = 'Long Hair';
const COLL_MID_LENGTH_HAIR = 'Mid Length Hair';
const COLL_BEARD_HEADS_ID = '347813183662'; // 'BEARD-HEADS' collection

// Helper: case-insensitive compare
function normalizeQuotes(str) {
    return String(str)
        .replace(/[‘’‛‹›']/g, "'")
        .replace(/[“”«»„‟]/g, '"')
        .replace(/[–—]/g, '-') // normalize en/em dashes
        .replace(/\s+/g, ' ') // collapse repeated whitespace
        .trim();
}

function equalsCI(a, b) {
    if (a == null || b == null) return false;
    return normalizeQuotes(a).toLowerCase().trim() === normalizeQuotes(b).toLowerCase().trim();
}

// Helper: test for presence of a collection by title, with fallback to tags if collections not present.
function hasCollection(product, collectionName) {
    if (!product || !collectionName) return false;

    // Case 1: simple array of collection objects
    if (Array.isArray(product.collections)) {
        if (
            product.collections.some(
                (c) => c && (equalsCI(c.title, collectionName) || equalsCI(c.handle, collectionName))
            )
        ) {
            return true;
        }
    }

    // Case 2: GraphQL connection: collections.edges[].node.{ title, handle }
    const edges = product.collections?.edges;
    if (Array.isArray(edges)) {
        if (
            edges.some(
                (e) => e?.node && (equalsCI(e.node.title, collectionName) || equalsCI(e.node.handle, collectionName))
            )
        ) {
            return true;
        }
    }

    // Case 3: GraphQL connection: collections.nodes[]
    const nodes = product.collections?.nodes;
    if (Array.isArray(nodes)) {
        if (nodes.some((n) => n && (equalsCI(n.title, collectionName) || equalsCI(n.handle, collectionName)))) {
            return true;
        }
    }

    // Fallback: treat collection name as a tag (merch sometimes mirrors names in tags)
    if (Array.isArray(product.tags)) {
        return product.tags.some((t) => equalsCI(t, collectionName));
    }
    return false;
}

// Match a collection by its immutable Shopify ID. Storefront GraphQL returns a
// GID (gid://shopify/Collection/123), while other product shapes may expose the
// numeric ID directly.
function hasCollectionId(product, collectionId) {
    if (!product || !collectionId) return false;

    const equalsCollectionId = (id) => id != null && String(id).split('/').pop() === String(collectionId);

    if (Array.isArray(product.collections)) {
        if (product.collections.some((collection) => collection && equalsCollectionId(collection.id))) return true;
    }

    const edges = product.collections?.edges;
    if (Array.isArray(edges)) {
        if (edges.some((edge) => edge?.node && equalsCollectionId(edge.node.id))) return true;
    }

    const nodes = product.collections?.nodes;
    if (Array.isArray(nodes)) {
        if (nodes.some((node) => node && equalsCollectionId(node.id))) return true;
    }

    return false;
}

// Helper: determine if an accessory advertises incompatibility via tags OR via the
// productWarningValue metafield (some products store the message in a metafield
// rather than as a tag). Returns an array of matching known incompatibility labels.
function accessoryIncompatibleLabels(product) {
    const found = new Set();
    if (!product) return [];
    // Check tags first
    if (Array.isArray(product.tags)) {
        ACCESSORY_INCOMPATIBLE_TAGS.forEach((incomp) => {
            if (product.tags.some((t) => equalsCI(t, incomp))) found.add(incomp);
        });
    }
    // Also inspect productWarningValue metafield value (if present)
    const meta = product.productWarningValue && product.productWarningValue.value;
    if (meta && typeof meta === 'string') {
        ACCESSORY_INCOMPATIBLE_TAGS.forEach((incomp) => {
            if (meta.toLowerCase().includes(incomp.toLowerCase())) found.add(incomp);
        });
    }
    return [...found];
}

function collectCollectionFlags(product) {
    return {
        overNeckFront: hasCollection(product, COLL_OVER_NECK_FRONT),
        overNeckBack: hasCollection(product, COLL_OVER_NECK_BACK),
        hairFront: hasCollection(product, COLL_HAIR_FRONT),
        hairBack: hasCollection(product, COLL_HAIR_BACK),
        longHair: hasCollection(product, COLL_LONG_HAIR),
        midLengthHair: hasCollection(product, COLL_MID_LENGTH_HAIR),
        frames: hasCollection(product, COLL_FRAMES) || hasCollection(product, COLL_FRAMES_PERSONALISED),
        incompatibleWithFrames: hasCollection(product, COLL_INCOMPATIBLE_WITH_FRAMES),
        keychains: hasCollection(product, COLL_KEYCHAINS),
        incompatibleWithKeychains: hasCollection(product, COLL_INCOMPATIBLE_WITH_KEYCHAINS),
        longBeard: hasCollection(product, COLL_LONG_BEARDS),
        incompatibleBeardHair: hasCollection(product, COLL_INCOMPATIBLE_BEARD_HAIRS),
        tiarasVeilsCompatibleHair: hasCollection(product, COLL_HAIRS_COMPATIBLE_WITH_TIARAS_VEILS),
    };
}

// Beard heads are currently also members of the front neck-accessory collection.
// Treating them as ordinary neck accessories would make a beard head conflict with
// itself and would also surface the generic "two neck accessories" warning.
function isBeardHead(product) {
    return hasCollectionId(product, COLL_BEARD_HEADS_ID);
}

function isOverNeckFrontAccessory(product) {
    return hasCollection(product, COLL_OVER_NECK_FRONT) && !isBeardHead(product);
}

function isOverNeckAccessory(product) {
    return isOverNeckFrontAccessory(product) || hasCollection(product, COLL_OVER_NECK_BACK);
}

// Primary checker (generic name now) — retains same signature.
export function checkIncompatibilities({ currentBuild, candidateProduct, candidateSection }) {
    if (!currentBuild || !candidateProduct || !candidateSection) return null;

    // Only evaluate incompatibilities when the user is actively adding / replacing a relevant builder part.
    if (!['hatsAndHair', 'heads', 'accessories', 'display', 'keychain'].includes(candidateSection)) {
        return null;
    }

    const existingHairEntry = currentBuild.selected?.hatsAndHair;
    const existingHeadEntry = currentBuild.selected?.heads;
    const existingAccessories = currentBuild.selected?.accessories || [];
    const existingDisplay = currentBuild.selected?.display;
    const existingKeychain = currentBuild.selected?.keychain;

    // Build prospective state after adding/replacing candidate
    const newHairEntry = candidateSection === 'hatsAndHair' ? { product: candidateProduct } : existingHairEntry;
    const newHeadEntry = candidateSection === 'heads' ? { product: candidateProduct } : existingHeadEntry;

    const newAccessories =
        candidateSection === 'accessories'
            ? [...existingAccessories, { product: candidateProduct }]
            : existingAccessories;

    const newDisplay = candidateSection === 'display' ? { product: candidateProduct } : existingDisplay;
    const newKeychain = candidateSection === 'keychain' ? { product: candidateProduct } : existingKeychain;

    const warnings = [];

    // ---- Existing legacy tag-based rules (kept) ----
    const hasTag = (tags, tag) => Array.isArray(tags) && tags.some((t) => equalsCI(t, tag));

    if (candidateSection === 'accessories') {
        if (newHairEntry?.product?.tags && hasTag(newHairEntry.product.tags, HAIR_WARNING_TAG)) {
            const offendingAccessoryTags = accessoryIncompatibleLabels(candidateProduct);
            if (offendingAccessoryTags && offendingAccessoryTags.length > 0) {
                warnings.push({
                    type: 'accessory-against-hair',
                    hairTitle: newHairEntry.product.title,
                    accessoryTitle: candidateProduct.title,
                    accessoryTags: offendingAccessoryTags,
                });
            }
        }
    } else if (candidateSection === 'hatsAndHair') {
        if (candidateProduct.tags && hasTag(candidateProduct.tags, HAIR_WARNING_TAG)) {
            const problematicAccessories = newAccessories.filter(
                (acc) => accessoryIncompatibleLabels(acc?.product).length > 0
            );
            if (problematicAccessories.length > 0) {
                warnings.push({
                    type: 'hair-against-accessories',
                    hairTitle: candidateProduct.title,
                    accessories: problematicAccessories.map((a) => ({ title: a.product.title, tags: a.product.tags })),
                });
            }
        }
    }

    // Merge duplicate legacy tag warnings (hair/accessories) into unified type to reduce noise
    {
        const tagBased = warnings.filter(
            (w) => w.type === 'accessory-against-hair' || w.type === 'hair-against-accessories'
        );
        if (tagBased.length > 1) {
            const hairTitle =
                tagBased.find((w) => w.hairTitle)?.hairTitle || newHairEntry?.product?.title || tagBased[0].hairTitle;
            const accessoryNames = new Set();
            tagBased.forEach((w) => {
                if (w.type === 'accessory-against-hair' && w.accessoryTitle) accessoryNames.add(w.accessoryTitle);
                if (w.type === 'hair-against-accessories' && Array.isArray(w.accessories)) {
                    w.accessories.forEach((a) => accessoryNames.add(a.title));
                }
            });
            // Remove the originals
            for (let i = warnings.length - 1; i >= 0; i--) {
                if (warnings[i].type === 'accessory-against-hair' || warnings[i].type === 'hair-against-accessories') {
                    warnings.splice(i, 1);
                }
            }
            warnings.push({
                type: 'hair-accessory-tag-warning',
                hairTitle,
                accessories: [...accessoryNames],
                message: `Heads up: that hair ("${hairTitle}") may not sit properly with: ${[...accessoryNames]
                    .map((n) => '"' + n + '"')
                    .join(', ')}`,
            });
        }
    }

    // ---- New collection-based rules ----
    const hairProduct = newHairEntry?.product;
    const hairFlags = hairProduct ? collectCollectionFlags(hairProduct) : {};

    const accessoryProducts = newAccessories.map((a) => a.product);
    // (If needed later we can derive accessory collection flags similarly.)

    // Beard heads may currently arrive through either the Heads section or the
    // Accessories/search flow, so inspect both prospective slots.
    const beardHeads = [newHeadEntry?.product, ...accessoryProducts].filter((p) => p && isBeardHead(p));
    const frontNeckAccessories = accessoryProducts.filter(isOverNeckFrontAccessory);

    if (beardHeads.length > 0 && frontNeckAccessories.length > 0) {
        warnings.push({
            type: 'beard-head-vs-over-neck-front',
            message: 'A beard head cannot be used with a front neck accessory.',
            beardHeads: beardHeads.map((p) => p.title.split('-')[0].trim()),
            accessories: frontNeckAccessories.map((p) => p.title.split('-')[0].trim()),
            candidateIsBeardHead: isBeardHead(candidateProduct),
        });
    }

    // Rule 1: Over the neck front + Hair front
    if (hairProduct && hairFlags.hairFront) {
        const overNeckFrontAcc = accessoryProducts.filter(isOverNeckFrontAccessory);
        if (overNeckFrontAcc.length > 0) {
            const list = overNeckFrontAcc.map((p) => `"${p.title}"`).join(', ');
            warnings.push({
                type: 'over-neck-front-vs-hair-front',
                message: `That front hair style "${hairProduct.title}" clashes with the front neck accessory ${list}. Please consider removing one of them.`,
                hair: hairProduct.title,
                accessories: overNeckFrontAcc.map((p) => p.title),
            });
        }
    }

    // Rule 2: Over the neck back + Hair back
    if (hairProduct && hairFlags.hairBack) {
        const overNeckBackAccRaw = accessoryProducts.filter((p) => hasCollection(p, COLL_OVER_NECK_BACK));
        const seenBack = new Set();
        const overNeckBackAcc = [];
        overNeckBackAccRaw.forEach((p) => {
            const base = p.title.split('-')[0].trim();
            if (!seenBack.has(base)) {
                seenBack.add(base);
                overNeckBackAcc.push(p);
            }
        });
        if (overNeckBackAcc.length > 0) {
            const list = overNeckBackAcc.map((p) => `"${p.title.split('-')[0].trim()}"`).join(', ');
            warnings.push({
                type: 'over-neck-back-vs-hair-back',
                message: `The hair style "${hairProduct.title
                    ?.split('-')[0]
                    .trim()}" doesn't fit with the back/neck accessory ${list}. Please consider removing one.`,
                hair: hairProduct.title,
                accessories: overNeckBackAcc.map((p) => p.title),
            });
        }
    }

    // Long Hair OR Mid Length Hair + any over-the-neck accessory (front or back)
    if (hairProduct && (hairFlags.longHair || hairFlags.midLengthHair)) {
        const overNeckAnyRaw = accessoryProducts.filter((p) => isOverNeckAccessory(p));
        const seenAny = new Set();
        const overNeckAny = [];
        overNeckAnyRaw.forEach((p) => {
            const base = p.title.split('-')[0].trim();
            if (!seenAny.has(base)) {
                seenAny.add(base);
                overNeckAny.push(p);
            }
        });
        if (overNeckAny.length > 0) {
            const list = overNeckAny.map((p) => `"${p.title.split('-')[0].trim()}"`).join(', ');
            warnings.push({
                type: 'long-mid-hair-vs-over-neck',
                message: `That hair style ("${hairProduct.title
                    .split('-')[0]
                    .trim()}") may clash with the neck accessory ${list}. Please consider removing one or choosing a shorter hair style.`,
                hair: hairProduct.title,
                accessories: overNeckAny.map((p) => p.title.split('-')[0].trim()),
            });
        }
    }

    // Rule 3: More than one over-the-neck accessory (front or back)
    const overNeckAccessories = accessoryProducts.filter((p) => isOverNeckAccessory(p));
    // Deduplicate identical accessories (by base title) to avoid duplicate entries when the same product object appears twice
    const seenOverNeck = new Set();
    const overNeckAccessoriesDistinct = [];
    overNeckAccessories.forEach((p) => {
        const base = p.title.split('-')[0].trim();
        if (!seenOverNeck.has(base)) {
            seenOverNeck.add(base);
            overNeckAccessoriesDistinct.push(p);
        }
    });
    if (overNeckAccessoriesDistinct.length > 1) {
        warnings.push({
            type: 'multiple-over-neck-accessories',
            message: `Only one neck accessory will fit properly. You currently have: ${overNeckAccessoriesDistinct
                .map((p) => `"${p.title.split('-')[0].trim()}"`)
                .join(', ')}. Please keep just one.`,
            accessories: overNeckAccessoriesDistinct.map((p) => p.title.split('-')[0].trim()),
        });
    }

    // Rule 4: Frames + Incompatible with Frames (can be hair, accessories, display, or keychain)
    const framesItems = [];
    const incompatibleWithFramesItems = [];

    if (hairProduct) {
        if (hairFlags.frames) framesItems.push(hairProduct);
        if (hairFlags.incompatibleWithFrames) incompatibleWithFramesItems.push(hairProduct);
    }
    accessoryProducts.forEach((p) => {
        if (hasCollection(p, COLL_FRAMES) || hasCollection(p, COLL_FRAMES_PERSONALISED)) framesItems.push(p);
        if (hasCollection(p, COLL_INCOMPATIBLE_WITH_FRAMES)) incompatibleWithFramesItems.push(p);
    });
    // Also check display and keychain slots (using prospective state)
    [newDisplay?.product, newKeychain?.product].filter(Boolean).forEach((p) => {
        if (hasCollection(p, COLL_FRAMES) || hasCollection(p, COLL_FRAMES_PERSONALISED)) framesItems.push(p);
        if (hasCollection(p, COLL_INCOMPATIBLE_WITH_FRAMES)) incompatibleWithFramesItems.push(p);
    });

    if (framesItems.length > 0 && incompatibleWithFramesItems.length > 0) {
        warnings.push({
            type: 'frames-incompatibility',
            message: `Those frames (${framesItems
                .map((p) => `"${p.title.split('-')[0].trim()}"`)
                .join(', ')}) can't be paired with ${incompatibleWithFramesItems
                .map((p) => `"${p.title.split('-')[0].trim()}"`)
                .join(', ')}. Please consider removing one of the conflicting items.`,
            frames: framesItems.map((p) => p.title.split('-')[0].trim()),
            incompatible: incompatibleWithFramesItems.map((p) => p.title.split('-')[0].trim()),
        });
    }

    // Rule 5: Keychains + Incompatible with KEYCHAINS (can be hair, accessories, display, or keychain)
    const keychainsItems = [];
    const incompatibleWithKeychainsItems = [];

    if (hairProduct) {
        const hairKeychainFlags = collectCollectionFlags(hairProduct);
        if (hairKeychainFlags.keychains) keychainsItems.push(hairProduct);
        if (hairKeychainFlags.incompatibleWithKeychains) incompatibleWithKeychainsItems.push(hairProduct);
    }
    accessoryProducts.forEach((p) => {
        if (hasCollection(p, COLL_KEYCHAINS)) keychainsItems.push(p);
        if (hasCollection(p, COLL_INCOMPATIBLE_WITH_KEYCHAINS)) incompatibleWithKeychainsItems.push(p);
    });
    // Also check display and keychain slots (using prospective state)
    [newDisplay?.product, newKeychain?.product].filter(Boolean).forEach((p) => {
        if (hasCollection(p, COLL_KEYCHAINS)) keychainsItems.push(p);
        if (hasCollection(p, COLL_INCOMPATIBLE_WITH_KEYCHAINS)) incompatibleWithKeychainsItems.push(p);
    });

    if (keychainsItems.length > 0 && incompatibleWithKeychainsItems.length > 0) {
        warnings.push({
            type: 'keychains-incompatibility',
            message: `Those keychains (${keychainsItems
                .map((p) => `"${p.title.split('-')[0].trim()}"`)
                .join(', ')}) can't be paired with ${incompatibleWithKeychainsItems
                .map((p) => `"${p.title.split('-')[0].trim()}"`)
                .join(', ')}. Please consider removing one of the conflicting items.`,
            keychains: keychainsItems.map((p) => p.title.split('-')[0].trim()),
            incompatible: incompatibleWithKeychainsItems.map((p) => p.title.split('-')[0].trim()),
        });
    }

    // Rule 6: Long Beards + incompatible beard hair
    if (hairProduct && hairFlags.incompatibleBeardHair) {
        const longBeards = accessoryProducts.filter((p) => hasCollection(p, COLL_LONG_BEARDS));
        if (longBeards.length > 0) {
            warnings.push({
                type: 'long-beard-vs-incompatible-hair',
                message: `The hair style "${hairProduct.title}" doesn't sit well with these long beards: ${longBeards
                    .map((p) => `"${p.title.split('-')[0].trim()}"`)
                    .join(', ')}. Please choose a different hair or remove the beard.`,
                hair: hairProduct.title,
                beards: longBeards.map((p) => p.title.split('-')[0].trim()),
            });
        }
    }
    // Also handle case where candidate is the beard being added after incompatible hair already selected
    if (!hairProduct && existingHairEntry?.product) {
        const existingHairFlags = collectCollectionFlags(existingHairEntry.product);
        if (existingHairFlags.incompatibleBeardHair && hasCollection(candidateProduct, COLL_LONG_BEARDS)) {
            warnings.push({
                type: 'long-beard-vs-incompatible-hair',
                message: `That hair ("${existingHairEntry.product.title}") doesn't work with the beard "${candidateProduct.title}". Please consider removing one of them.`,
                hair: existingHairEntry.product.title,
                beards: [candidateProduct.title.split('-')[0].trim()],
            });
        }
    }

    // Rule 7: Tiaras and Veil (compatible with hair collection) require a compatible hair selection
    // Trigger when at least one such accessory is selected AND the chosen hair is NOT in the compatible hairs collection.
    if (hairProduct) {
        const tiaraVeilAccessories = accessoryProducts.filter((p) =>
            hasCollection(p, COLL_TIARAS_VEIL_COMPATIBLE_WITH_HAIR)
        );
        if (tiaraVeilAccessories.length > 0 && !hasCollection(hairProduct, COLL_HAIRS_COMPATIBLE_WITH_TIARAS_VEILS)) {
            warnings.push({
                type: 'tiara-veil-vs-incompatible-hair',
                message: `That accessory (${tiaraVeilAccessories
                    .map((p) => `"${p.title.split('-')[0].trim()}"`)
                    .join(
                        ', '
                    )}) needs a compatible hair style. Please pick a hair from the 'Hairs Compatible with Tiaras and Veils' collection or remove the tiara/veil.`,
                hair: hairProduct.title,
                accessories: tiaraVeilAccessories.map((p) => p.title.split('-')[0].trim()),
            });
        }
    }

    if (warnings.length === 0) return null;

    // Attach the candidate's base title (and frame flag) to every warning so
    // formatIncompatibilityAlert can personalise the message with "[Part name]".
    const candidateBaseTitle = candidateProduct.title.split('-')[0].trim();
    const candidateIsFrameProduct =
        hasCollection(candidateProduct, COLL_FRAMES) || hasCollection(candidateProduct, COLL_FRAMES_PERSONALISED);
    warnings.forEach((w) => {
        w.candidateTitle = candidateBaseTitle;
        if (w.type === 'frames-incompatibility') {
            w.candidateIsFrame = candidateIsFrameProduct;
        }
    });

    // Only keep warnings that directly involve the candidate product being added now.
    // This prevents resurfacing historic conflicts the user has already acknowledged
    // when they add an unrelated new item.
    const candidateTitle = candidateProduct.title;
    const candidateIsHair = candidateSection === 'hatsAndHair';
    const candidateIsHead = candidateSection === 'heads';
    const candidateIsAccessory = candidateSection === 'accessories';
    const candidateIsDisplay = candidateSection === 'display';
    const candidateIsKeychain = candidateSection === 'keychain';

    const warningInvolvesCandidate = (w) => {
        // Legacy types
        if (w.type === 'accessory-against-hair') {
            return candidateIsAccessory && w.accessoryTitle === candidateTitle;
        }
        if (w.type === 'hair-against-accessories') {
            return candidateIsHair; // candidate hair conflicts with existing accessories
        }
        if (w.type === 'hair-accessory-tag-warning') {
            if (candidateIsHair && w.hairTitle === candidateTitle) return true;
            if (candidateIsAccessory && w.accessories?.some((a) => a === candidateTitle)) return true;
            return false;
        }
        // Collection based types
        if (w.type === 'beard-head-vs-over-neck-front') {
            if ((candidateIsHead || candidateIsAccessory) && w.candidateIsBeardHead) return true;
            if (candidateIsAccessory && w.accessories?.some((t) => candidateTitle.startsWith(t))) return true;
            return false;
        }
        if (w.type === 'over-neck-front-vs-hair-front' || w.type === 'over-neck-back-vs-hair-back') {
            if (candidateIsHair && w.hair === candidateTitle) return true;
            if (candidateIsAccessory && w.accessories?.some((t) => t === candidateTitle)) return true;
            return false;
        }
        if (w.type === 'multiple-over-neck-accessories') {
            // Only relevant when adding an accessory that contributes to the over-neck group
            if (!candidateIsAccessory) return false;
            return w.accessories?.some((t) => candidateTitle.startsWith(t));
        }
        if (w.type === 'frames-incompatibility') {
            if (candidateIsHair && w.frames?.some((t) => candidateTitle.startsWith(t))) return true;
            if (candidateIsHair && w.incompatible?.some((t) => candidateTitle.startsWith(t))) return true;
            if (candidateIsAccessory || candidateIsDisplay || candidateIsKeychain) {
                const matchFrames = w.frames?.some((t) => candidateTitle.startsWith(t));
                const matchIncomp = w.incompatible?.some((t) => candidateTitle.startsWith(t));
                return matchFrames || matchIncomp;
            }
            return false;
        }
        if (w.type === 'keychains-incompatibility') {
            if (candidateIsHair && w.keychains?.some((t) => candidateTitle.startsWith(t))) return true;
            if (candidateIsHair && w.incompatible?.some((t) => candidateTitle.startsWith(t))) return true;
            if (candidateIsAccessory || candidateIsDisplay || candidateIsKeychain) {
                const matchKeychains = w.keychains?.some((t) => candidateTitle.startsWith(t));
                const matchIncomp = w.incompatible?.some((t) => candidateTitle.startsWith(t));
                return matchKeychains || matchIncomp;
            }
            return false;
        }
        if (w.type === 'long-beard-vs-incompatible-hair') {
            if (candidateIsHair && w.hair === candidateTitle) return true;
            if (candidateIsAccessory && w.beards?.some((t) => candidateTitle.startsWith(t))) return true;
            return false;
        }
        if (w.type === 'tiara-veil-vs-incompatible-hair') {
            if (candidateIsHair && w.hair === candidateTitle) return true;
            if (candidateIsAccessory && w.accessories?.some((t) => candidateTitle.startsWith(t))) return true;
            return false;
        }
        if (w.type === 'long-mid-hair-vs-over-neck') {
            if (candidateIsHair && w.hair === candidateTitle) return true;
            if (candidateIsAccessory) {
                const candidateBase = candidateTitle.split('-')[0].trim();
                if (w.accessories?.some((t) => candidateBase === t)) return true;
            }
            return false;
        }
        // Default: keep only if we can't determine (conservative) – but in practice all types are covered above.
        return true;
    };

    const filtered = warnings.filter(warningInvolvesCandidate);
    if (filtered.length === 0) return null; // Nothing relevant to this action

    // Aggregate duplicate warnings that reference the same involved product set (hair + accessories etc.)
    const canonicalKeyForWarning = (w) => {
        const parts = new Set();
        if (w.hair) parts.add(`H:${w.hair.split('-')[0].trim()}`);
        if (Array.isArray(w.accessories)) w.accessories.forEach((a) => parts.add(`A:${a.split('-')[0].trim()}`));
        if (Array.isArray(w.beards)) w.beards.forEach((b) => parts.add(`B:${b.split('-')[0].trim()}`));
        if (Array.isArray(w.beardHeads)) w.beardHeads.forEach((b) => parts.add(`BH:${b.split('-')[0].trim()}`));
        if (Array.isArray(w.frames)) w.frames.forEach((f) => parts.add(`F:${f.split('-')[0].trim()}`));
        if (Array.isArray(w.keychains)) w.keychains.forEach((k) => parts.add(`K:${k.split('-')[0].trim()}`));
        if (Array.isArray(w.incompatible)) w.incompatible.forEach((i) => parts.add(`I:${i.split('-')[0].trim()}`));
        return [...parts].sort().join('|');
    };

    const grouped = new Map();
    filtered.forEach((w) => {
        const key = canonicalKeyForWarning(w);
        if (!grouped.has(key)) grouped.set(key, []);
        grouped.get(key).push(w);
    });

    const aggregated = [];
    grouped.forEach((groupWarnings) => {
        if (groupWarnings.length === 1) {
            aggregated.push(groupWarnings[0]);
        } else {
            const sample = groupWarnings[0];
            const hair = sample.hair ? sample.hair.split('-')[0].trim() : null;
            const allItems = new Set();
            groupWarnings.forEach((gw) => {
                ['accessories', 'beards', 'beardHeads', 'frames', 'keychains', 'incompatible'].forEach((k) => {
                    if (Array.isArray(gw[k])) gw[k].forEach((v) => allItems.add(v.split('-')[0].trim()));
                });
            });
            const itemsArr = [...allItems].sort();
            let message;
            if (hair && itemsArr.length) {
                message = `Those selected items (hair: "${hair}"; with ${itemsArr
                    .map((i) => `"${i}"`)
                    .join(
                        ', '
                    )}) have compatibility conflicts. Please consider removing one of the conflicting pieces.`;
            } else if (hair) {
                message = `The selected hair style ("${hair}") conflicts with another chosen piece. Please adjust.`;
            } else if (itemsArr.length) {
                message = `Some of those selected items (${itemsArr
                    .map((i) => `"${i}"`)
                    .join(', ')}) can't all be used together.`;
            } else {
                message = 'Some selected items are incompatible.';
            }
            aggregated.push({
                type: 'aggregated-incompatibility',
                message,
                hair: hair || undefined,
                accessories: itemsArr,
                originalTypes: groupWarnings.map((gw) => gw.type),
            });
        }
    });

    // Replace warnings with aggregated subset
    warnings.length = 0;
    aggregated.forEach((w) => warnings.push(w));

    // Backwards compatibility: if only one legacy (old types) warning and no new types, return that object as before
    const onlyLegacy =
        warnings.length === 1 &&
        (warnings[0].type === 'accessory-against-hair' ||
            warnings[0].type === 'hair-against-accessories' ||
            warnings[0].type === 'hair-accessory-tag-warning' ||
            warnings[0].type === 'aggregated-incompatibility');

    if (onlyLegacy) return warnings[0];

    // Return aggregated
    return { type: 'multiple', warnings };
}

export function formatIncompatibilityAlert(data) {
    if (!data) return { title: 'Alert', message: '' };

    const formatSingle = (d) => {
        const partName =
            d.candidateTitle ||
            d.accessoryTitle?.split('-')[0].trim() ||
            d.hairTitle?.split('-')[0].trim() ||
            'This item';

        if (d.type === 'beard-head-vs-over-neck-front') {
            if (d.candidateIsBeardHead) {
                const accessoryNames = d.accessories?.map((name) => `"${name}"`).join(', ') || 'that neck accessory';
                return {
                    title: 'Heads up!',
                    message: `${partName} isn't compatible with ${accessoryNames}. You may want to choose a printed beard head instead. If you continue, we won't be able to add ${partName} to your figure.`,
                };
            }
            return {
                title: 'Heads up!',
                message: `${partName} isn't compatible with any heads with LEGO beards. You may want to choose a printed beard head instead. If you continue, we won't be able to add ${partName} to your figure.`,
            };
        }

        if (d.type === 'multiple-over-neck-accessories') {
            const otherAccessories = (d.accessories || []).filter((name) => {
                const baseName = name.split('-')[0].trim();
                return baseName !== partName;
            });
            const otherAccessoryText = otherAccessories.length
                ? otherAccessories.map((name) => `"${name}"`).join(', ')
                : 'another selected neck accessory';

            return {
                title: 'Heads up!',
                message: `${partName} isn't compatible with ${otherAccessoryText}. Only one neck accessory can be used on your figure. If you continue, we won't be able to add ${partName} to your figure.`,
            };
        }

        // Hair / hat incompatibility types
        if (
            d.type === 'accessory-against-hair' ||
            d.type === 'hair-against-accessories' ||
            d.type === 'hair-accessory-tag-warning' ||
            d.type === 'over-neck-front-vs-hair-front' ||
            d.type === 'over-neck-back-vs-hair-back' ||
            d.type === 'long-mid-hair-vs-over-neck' ||
            d.type === 'long-beard-vs-incompatible-hair' ||
            d.type === 'tiara-veil-vs-incompatible-hair'
        ) {
            return {
                title: 'Heads up!',
                message: `${partName} isn't compatible with your selected hair/hat piece. If you continue, we won't be able to add it to your figure.`,
            };
        }

        // Keychain incompatibility
        if (d.type === 'keychains-incompatibility') {
            // When the candidate IS the keychain, reference the other (incompatible) items instead
            const candidateIsKeychain = d.keychains?.some((k) => partName === k);
            if (candidateIsKeychain && d.incompatible?.length > 0) {
                const otherItems = d.incompatible.map((i) => `"${i}"`).join(', ');
                return {
                    title: 'Heads up!',
                    message: `${otherItems} isn't compatible with a keychain. If you continue, we won't be able to turn your figure into a keychain.`,
                };
            }
            return {
                title: 'Heads up!',
                message: `${partName} isn't compatible with a keychain. If you continue, we won't be able to turn your figure into a keychain.`,
            };
        }

        // Frames incompatibility
        if (d.type === 'frames-incompatibility') {
            if (d.candidateIsFrame) {
                return {
                    title: 'Uh oh!',
                    message: `${partName} won't fit inside this frame. If you continue, you won't be able to display it with your figure(s).`,
                };
            }
            return {
                title: 'Uh oh!',
                message: `${partName} won't fit inside your frame. If you continue, you won't be able to display it with your figure(s).`,
            };
        }

        // Aggregated / fallback
        if (d.type === 'aggregated-incompatibility') {
            return {
                title: 'Heads up!',
                message:
                    d.message || "Some of these items aren't compatible. If you continue, some parts may not be added.",
            };
        }

        return {
            title: 'Heads up!',
            message: d.message || "Some of these items aren't compatible.",
        };
    };

    if (Array.isArray(data)) {
        const results = data.map(formatSingle);
        return {
            title: results[0]?.title || 'Heads up!',
            message: results.map((r) => r.message).join('\n\n'),
        };
    }
    if (data.type === 'multiple' && Array.isArray(data.warnings)) {
        const results = data.warnings.map(formatSingle);
        return {
            title: results[0]?.title || 'Heads up!',
            message: results.map((r) => r.message).join('\n\n'),
        };
    }
    return formatSingle(data);
}

export default {
    checkIncompatibilities,
    formatIncompatibilityAlert,
};
